import clsx from "clsx";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

// Initials from a name (first + last), else the email prefix — the one helper
// for every initials avatar; import it rather than re-deriving it.
export function getInitials(name, email) {
  if (name) {
    const nameTrimmed = name.trim();
    if (nameTrimmed) {
      // Ensure name is not empty after trimming
      const parts = nameTrimmed.split(" ").filter((part) => part.length > 0);
      if (parts.length > 1) {
        // Multiple parts: use first letter of first and last
        const firstInitial = parts[0][0] || "";
        const lastInitial = parts[parts.length - 1][0] || "";
        if (firstInitial && lastInitial) {
          return (firstInitial + lastInitial).toUpperCase();
        } else if (firstInitial) {
          return firstInitial.toUpperCase(); // Fallback if only first part has letter
        } else if (lastInitial) {
          return lastInitial.toUpperCase(); // Fallback if only last part has letter
        }
      } else if (parts.length === 1) {
        // Single part: use first letter only
        return parts[0][0].toUpperCase();
      }
    }
  }
  // Email fallback if name didn't yield initials
  if (email) {
    const emailPrefix = email.split("@")[0];
    if (emailPrefix.length > 0) {
      return emailPrefix[0].toUpperCase();
    }
  }
  return "?"; // Ultimate fallback
}

const UserAvatar = ({
  userImage,
  userName,
  userEmail,
  className,
  userNameClass,
  // optional: keep for API compatibility, not used with Radix Avatar
  fallbackSrc = "/placeholder.png",
}) => {
  // Determine if the component's own name span should be visible
  const showInternalNameSpan =
    (userName || userEmail) && !userNameClass?.includes("hidden");
  const displayName = userName || userEmail?.split("@")[0]; // Name used if internal span is shown
  const initials = getInitials(userName, userEmail); // Get 1 or 2 initials

  return (
    <div className={clsx("inline-flex items-center", className)}>
      {/* Avatar Part: Image or Initials Circle */}
      <Avatar className="w-7 h-7 shadow-xs">
        <AvatarImage src={userImage} alt="profile picture" />
        <AvatarFallback className="bg-indigo-700 text-white text-sm font-medium">
          <span
            className={clsx(initials.length > 1 ? "text-[10px]" : "text-xs")}
          >
            {initials}
          </span>
        </AvatarFallback>
      </Avatar>

      {/* Optional Internal Name Span (hidden when called from Leaderboard) */}
      {showInternalNameSpan && (
        <span
          className={clsx(
            "inline-block my-auto text-sm truncate max-w-28 lg:max-w-64 ml-2", // Margin added when internal name shown
            userNameClass, // Apply provided class (which might hide it)
          )}
        >
          {displayName}
        </span>
      )}
    </div>
  );
};

export default UserAvatar;
